import {
  BrowserPlugin,
  newTracker,
  trackPageView as spTrackPageView,
  trackSelfDescribingEvent as spTrackSelfDescribing,
} from '@snowplow/browser-tracker';
import {
  trackPageView as wvTrackPageView,
  trackSelfDescribingEvent as wvTrackSelfDescribing,
} from '@snowplow/webview-tracker';
import { User } from '@/api/types/public';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { ZL_USERINFO } from '@/utils/constants';
import { isInZeloolApp } from '@/utils/tools';
import { resolveSnowplowPixels } from '../snowplowPixelMapping';
import { Provider, ProviderType, TrackingEvent } from '../types';
import { createSnowplowGzipFetch } from './snowplowGzipFetch';

const TRACKER_NAMESPACE = 'sp1';
const ZEELOOL_SCHEMA = 'iglu:com.zeelool/event/jsonschema/1-0-0';
const PUBLIC_PROPS_SCHEMA = 'iglu:com.zeelool/public_properties/jsonschema/1-0-0';
const PAGE_VIEW_EVENT = 'page_view';

interface SnowplowConfig {
  collectorUrl: string;
  appId: string;
  platform: string;
  platformName: string;
  envName: string;
  isEnabled?: boolean;
  gzipEnabled?: boolean;
}

/**
 * 读取 localStorage 中的用户信息，失败时返回空对象
 */
function getZlUserInfo(): User {
  try {
    return JSON.parse(localStorage.getItem(ZL_USERINFO) || '{}') || {};
  } catch {
    return {};
  }
}

function resolveCollectorUrl(collectorUrl: string): string {
  if (collectorUrl.trim() === '/') {
    return window.location.origin;
  }

  return collectorUrl.replace(/\/+$/, '');
}

export class SnowplowProvider implements Provider {
  type = ProviderType.SNOWPLOW;
  isEnabled: boolean;
  loggingEnabled = false;
  private initialized = false;
  private config: SnowplowConfig;

  constructor(config: SnowplowConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? false;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // App 内无需初始化浏览器 tracker，webview-tracker 直接调用函数即可
      if (isInZeloolApp()) {
        this.initialized = true;
        return true;
      }

      // 去掉尾部斜杠，避免与 postPath 拼接时产生 double-slash
      const collectorUrl = resolveCollectorUrl(this.config.collectorUrl);

      newTracker(TRACKER_NAMESPACE, collectorUrl, {
        appId: this.config.appId,
        platform: 'web',
        eventMethod: 'post',
        postPath: '/zlmkt-metrics/collect',
        // gzip 封装仅作用于 browser-tracker 网络发送链路，App webview 路径不经过这里
        ...(this.config.gzipEnabled !== false
          ? { customFetch: createSnowplowGzipFetch(fetch) }
          : {}),
        plugins: [
          {
            // 公共属性作为 Global Context 附加到每条事件的 co 字段
            contexts: () => [{ schema: PUBLIC_PROPS_SCHEMA, data: this.getCommonProperties() }],
          } as BrowserPlugin,
        ],
        contexts: {
          webPage: true,
        },
      });

      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('SnowplowProvider: Failed to initialize:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      this.dispatchEvent(event);
    } catch (error) {
      logger.error('SnowplowProvider: tracking error:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  enableLog(): void {
    this.loggingEnabled = true;
  }

  disableLog(): void {
    this.loggingEnabled = false;
  }

  /**
   * 统一分发方法：根据是否在 App 内路由到 webview-tracker 或 browser-tracker
   */
  private dispatchEvent(event: TrackingEvent): void {
    const inApp = isInZeloolApp();
    const eventName = event.eventName;

    // 公共属性与事件属性合并后一起上报，确保每条事件都有完整数据
    const commonProps = this.getCommonProperties();
    const filteredData = this.filterDollarKeys(event.properties ?? {});

    const mergedData = {
      event_name: eventName,
      properties: {
        ...commonProps, // 公共属性（与神策 registerPage 保持一致）
        ...filteredData, // 事件私有属性可覆盖公共属性同名字段
        ...(event.eventId ? { event_id: event.eventId } : {}),
      },
      pixel: resolveSnowplowPixels(eventName),
    };

    if (this.loggingEnabled) {
      logger.log('SnowplowProvider mergedData:', mergedData);
    }

    if (eventName === PAGE_VIEW_EVENT) {
      const pageViewContext = [{ schema: PUBLIC_PROPS_SCHEMA, data: commonProps }];
      inApp
        ? wvTrackPageView({}, [pageViewContext])
        : spTrackPageView({ context: pageViewContext });
      return;
    }

    const sdEvent = {
      event: {
        schema: ZEELOOL_SCHEMA,
        data: mergedData,
      },
    };

    inApp ? wvTrackSelfDescribing(sdEvent) : spTrackSelfDescribing(sdEvent);
  }

  /**
   * 实时读取与神策 registerCommonProperties() 对应的公共属性
   * 每次事件触发时求值，保证实时性
   */
  private getCommonProperties(): Record<string, unknown> {
    const userInfo = getZlUserInfo();
    const channelAttr = (
      window as Window &
        typeof globalThis & {
          latestChannelAttribute?: Record<string, unknown> & { expires?: number };
        }
    ).latestChannelAttribute;
    const channelValid = (channelAttr?.expires ?? 0) > Date.now() || channelAttr?.expires === -1;

    const data: Record<string, unknown> = {
      pub_platform: this.config.platform,
      pub_platform_name: this.config.platformName,
      pub_client: this.config.platform,
      pub_env_name:
        this.config.envName === 'production' ? 'production_is_nextjs' : this.config.envName,
      pub_member_level: `${(userInfo.member_level ?? -2) >= -1 ? userInfo.member_level : '9999'}`,
      pub_is_paid: !!userInfo.last_pay_time,
      pub_is_login: !!localStorage.getItem('zl-token'),
    };

    if (channelValid && channelAttr) {
      if (channelAttr.pub_zl_utm_source) data.pub_zl_utm_source = channelAttr.pub_zl_utm_source;
      if (channelAttr.pub_zl_utm_medium) data.pub_zl_utm_medium = channelAttr.pub_zl_utm_medium;
      if (channelAttr.pub_zl_utm_campaign)
        data.pub_zl_utm_campaign = channelAttr.pub_zl_utm_campaign;
      if (channelAttr.pub_zl_utm_campaignid)
        data.pub_zl_utm_campaignid = channelAttr.pub_zl_utm_campaignid;
      if (channelAttr.pub_zl_referrer) data.pub_zl_referrer = channelAttr.pub_zl_referrer;
      if (channelAttr.pub_zl_page) data.pub_zl_page = channelAttr.pub_zl_page;
    }

    if (channelAttr?.pub_landing_page) {
      data.pub_landing_page = channelAttr.pub_landing_page;
    }

    try {
      data.distinct_id = window.sensorsDataAnalytic201505?.store?.getDistinctId();
      data.anonymous_id = window.sensorsDataAnalytic201505?.quick('getAnonymousID');
    } catch (error) {
      logger.error('Failed to get distinct_id and anonymous_id:', error);
    }

    return data;
  }

  /**
   * 过滤掉 $ 开头的属性（神策内部属性，不上报给 Snowplow）
   * 同时过滤空值，与神策行为保持一致
   */
  private filterDollarKeys(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key.startsWith('$')) continue;
      if (value === '' || value === null || value === undefined) continue;
      if (value === 'null' || value === 'undefined') continue;
      if (Array.isArray(value) && value.length === 0) continue;
      result[key] = value;
    }
    return result;
  }
}
